import type { BorrowerDetail, MerchantBankAccount, PlaidInstitution } from "@/types/borrower";
import type {
  AnalysisOutput,
  UnderwritingDataSource,
  UnderwritingDecision,
} from "@/types/underwriting";

/** What the Final Underwriting tab shows in place of the Plaid asset report. */
export type PlaidPdfSlotMode = "manual-login" | "no-report";

/** App statuses the Plaid pipeline is still expected to produce an analysis from. */
export const PLAID_PROCESSING_BANNER_STATUSES: BorrowerDetail["app_status"][] = [
  "contract_signed",
  "plaid_initiated",
  "plaid_completed",
];

/** Newest decision from one lane, or null when that lane never ran. */
export function latestDecision(
  borrower: BorrowerDetail | null | undefined,
  dataSource: UnderwritingDataSource
): UnderwritingDecision | null {
  // Rows predating the Plaid lane carry no data_source and are statements.
  const lane = (borrower?.decisions ?? []).filter(
    (d) => (d.data_source ?? "statements") === dataSource
  );
  lane.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  return lane[0] ?? null;
}

/** Analysis the newest decision of one lane carries, if it has landed one. */
export function latestAnalysis(
  borrower: BorrowerDetail | null | undefined,
  dataSource: UnderwritingDataSource
): AnalysisOutput | undefined {
  return latestDecision(borrower, dataSource)?.detailed_results?.analysis;
}

/**
 * True while no finished Plaid analysis covers the latest asset report, i.e. one
 * is still owed for it.
 *
 * The decision row is unique per (borrower, data_source), so a rerun updates the
 * existing row rather than inserting one: its ``created_at`` stays pinned to the
 * very first analysis and can never say whether anything ran for *this* report.
 * ``processing_completed_at`` can — every lane stamps it when an analysis
 * finishes (success or failure), and the Plaid lane clears it when a rerun
 * starts. A cleared stamp means the run in flight owes us its result, whatever
 * the timestamps say; only rows written before the field existed (never stamped,
 * never processing) fall back to ``created_at`` to keep their old behaviour.
 */
export function isPlaidAnalysisBehindReport(borrower: BorrowerDetail): boolean {
  const reportCreatedAt = borrower.plaid_report_created_at;
  const decision = latestDecision(borrower, "plaid");
  if (!reportCreatedAt) return !decision;
  if (!decision) return true;
  if (decision.decision_status === "processing") return true;
  const analyzedAt = decision.processing_completed_at ?? decision.created_at;
  return new Date(analyzedAt).getTime() < new Date(reportCreatedAt).getTime();
}

/**
 * True while the Plaid pipeline still owes this borrower an analysis: the report
 * is generating, a run is in flight, or nothing has finished for the newest
 * report yet. A failed report is terminal — the retry controls take over.
 *
 * Single source for the tab's processing state: the placeholder and controls it
 * gates, and the poll loop, which stops exactly when this goes false.
 */
export function isPlaidPipelineActive(borrower: BorrowerDetail | null | undefined): boolean {
  if (!borrower) return false;
  if (borrower.plaid_report_status === "failed") return false;
  if (borrower.plaid_report_status === "processing") return true;
  if (latestDecision(borrower, "plaid")?.decision_status === "processing") return true;
  if (borrower.plaid_report_status === "ready") return isPlaidAnalysisBehindReport(borrower);

  // No report to compare against yet: owed until an analysis is on screen.
  return (
    PLAID_PROCESSING_BANNER_STATUSES.includes(borrower.app_status) &&
    !latestAnalysis(borrower, "plaid")
  );
}

/**
 * Whether the statements (AI Analysis) decision should stand in for a missing
 * Plaid analysis, so the deal stays fundable/declinable from this tab.
 *
 * Deliberately independent of ``plaid_manual_override``: a deal can reach final
 * underwriting without a Plaid report because it was forced past a stuck flow,
 * because ``requires_plaid`` is false, or because staff moved it by hand, and
 * none of those should land on a dead placeholder. While Plaid collection or
 * processing is still in flight, PlaidStatusBanner owns the messaging instead.
 */
export function shouldUseStatementsStandIn(input: {
  hasPlaidAnalysis: boolean;
  hasStatementsAnalysis: boolean;
  plaidProcessing: boolean;
}): boolean {
  const { hasPlaidAnalysis, hasStatementsAnalysis, plaidProcessing } = input;
  return !hasPlaidAnalysis && !plaidProcessing && hasStatementsAnalysis;
}

/**
 * Marker shown in the PDF slot while standing in. Only a forced deal claims a
 * manual login; anything else simply has no report to preview.
 */
export function plaidPdfSlotMode(plaidManualOverride: boolean): PlaidPdfSlotMode {
  return plaidManualOverride ? "manual-login" : "no-report";
}

export interface BankListRow {
  key: string;
  label: string;
  isPrimary: boolean;
}

export interface BankListGroup {
  key: string;
  institution: PlaidInstitution | null;
  rows: BankListRow[];
}

function accountLabel(name: string, last4: string): string {
  return last4 ? `${name} ••${last4}` : name;
}

function accountRow(account: MerchantBankAccount): BankListRow {
  return {
    key: `account-${account.id}`,
    label: accountLabel(
      account.bank_name || account.name_on_account || "Account",
      (account.account_mask || account.account_number || "").slice(-4)
    ),
    isPrimary: account.is_primary,
  };
}

/**
 * Summary accounts Plaid Auth has no row for. Masks are not unique within an
 * Item, so each on-file row covers exactly one summary account with its mask.
 * A blank mask cannot be told apart from an on-file row, so it only shows
 * when the connection has none.
 */
function uncoveredSummaryAccounts(
  summary: PlaidInstitution["accounts"],
  onFile: readonly MerchantBankAccount[]
): PlaidInstitution["accounts"] {
  if (onFile.length === 0) return summary;
  const unclaimed = new Map<string, number>();
  for (const { account_mask: mask } of onFile) {
    if (mask) unclaimed.set(mask, (unclaimed.get(mask) ?? 0) + 1);
  }
  return summary.filter(({ mask }) => {
    if (!mask) return false;
    const left = unclaimed.get(mask) ?? 0;
    if (left > 0) unclaimed.set(mask, left - 1);
    return left === 0;
  });
}

/**
 * Accounts on file grouped under the Plaid connection they came from, plus the
 * connection's own account summary for accounts Plaid Auth has no row for.
 * Accounts with no listed connection (manual entry) trail in one ungrouped
 * block.
 */
export function groupBankAccounts(
  institutions: readonly PlaidInstitution[],
  accounts: readonly MerchantBankAccount[]
): BankListGroup[] {
  const institutionIds = new Set(institutions.map((inst) => inst.id));
  const groups: BankListGroup[] = institutions.map((inst) => {
    const onFile = accounts.filter((a) => a.plaid_item === inst.id);
    const summaryOnly = uncoveredSummaryAccounts(inst.accounts, onFile).map((a, i) => ({
      key: `summary-${inst.id}-${i}`,
      label: accountLabel(a.name || "Account", a.mask ?? ""),
      isPrimary: false,
    }));
    return {
      key: `item-${inst.id}`,
      institution: inst,
      rows: [...onFile.map(accountRow), ...summaryOnly],
    };
  });
  const unlisted = accounts.filter(
    (a) => a.plaid_item == null || !institutionIds.has(a.plaid_item)
  );
  if (unlisted.length > 0) {
    groups.push({ key: "unlisted", institution: null, rows: unlisted.map(accountRow) });
  }
  return groups;
}
